/**
 * The journal of a report: who changed which section, and when.
 *
 * Written by the server on every save, so it is read-only here. An entry stands
 * for one person's run of changes to one section: it is created when they first
 * change it and kept up to date until they stop, so it has a start and an end.
 */

import type { JsonObject } from '@/domain/json'

export type ChangeAction = 'added' | 'modified' | 'moved' | 'removed'

export interface JournalEntry {
  readonly id: number
  readonly sectionId: string
  readonly kind: string
  /** The section's title when it was changed: what to call it once it is gone. */
  readonly title: string
  readonly action: ChangeAction
  readonly author: string
  /** `edit` for an ordinary save, `restore` for going back to a version. */
  readonly origin: 'edit' | 'restore'
  /** When the first of the saves this entry stands for was made. */
  readonly startedAt: string
  /** When the last was. */
  readonly endedAt: string
  /**
   * What the section said afterwards (before, for a removal). Only where the
   * entry was asked for with its content; null when the section was too large
   * to keep a copy of, and for a move.
   */
  readonly content?: JsonObject | null
  /** What the section said before, kept for the first change of a block only. */
  readonly before?: JsonObject | null
}

/** The stored shape of an entry, as the API sends it. */
export interface StoredEntry {
  id: number
  section_id: string
  kind: string
  title: string
  action: string
  author: string
  origin: string
  created_at: string
  updated_at: string
  content?: JsonObject | null
  before?: JsonObject | null
}

const ACTIONS: readonly ChangeAction[] = ['added', 'modified', 'moved', 'removed']

export function readEntry(stored: StoredEntry): JournalEntry {
  const action = ACTIONS.find((one) => one === stored.action) ?? 'modified'
  return {
    id: stored.id,
    sectionId: stored.section_id,
    kind: stored.kind,
    title: stored.title,
    action,
    author: stored.author,
    origin: stored.origin === 'restore' ? 'restore' : 'edit',
    startedAt: stored.created_at,
    endedAt: stored.updated_at,
    ...(stored.content !== undefined ? { content: stored.content } : {}),
    ...(stored.before ? { before: stored.before } : {}),
  }
}
